import useSWR from "swr";
import { presignAsset, presignAssetById } from "@/lib/api";

export interface AssetUrlState {
  /** Resolved, short-lived signed media URL. `null` while resolving or on failure. */
  url: string | null;
  isLoading: boolean;
  error: unknown;
}

/**
 * Resolve a storage key into a short-lived, signed media URL via the auth'd
 * `/api/assets/presign` endpoint.
 *
 * Why: <img>/<audio>/<video> tags cannot send the `Authorization` header, so
 * pointing them at `/api/assets/proxy/{key}` always 401s. This hook fetches a
 * presigned (S3/MinIO) URL with the Bearer token once per key, caches it via
 * SWR, and refreshes it well before the server-side ~1h expiry.
 *
 * Pass `null`/`undefined` to disable (e.g. while an asset is still pending) —
 * the request is skipped and `url` stays `null`.
 */
export function useAssetUrl(key: string | null | undefined): AssetUrlState {
  const { data, error, isLoading } = useSWR<string>(
    key ? ["asset-presign", key] : null,
    () => presignAsset(key as string),
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      shouldRetryOnError: false,
      dedupingInterval: 5 * 60_000,
      // Presigned URLs live ~1h server-side; refresh comfortably before that.
      refreshInterval: 45 * 60_000,
      // Presigned URLs expire after an hour. Without this the interval does not
      // fire in a hidden tab, so every image 403s when the user comes back.
      refreshWhenHidden: true,
    }
  );

  return { url: data ?? null, isLoading: Boolean(key) && isLoading, error };
}

/**
 * Same exchange keyed by asset id, for references that carry no storage key —
 * frozen job parameters only record `asset_id`.
 */
export function useAssetIdUrl(assetId: string | null | undefined): AssetUrlState {
  const { data, error, isLoading } = useSWR<string>(
    assetId ? ["asset-presign-id", assetId] : null,
    () => presignAssetById(assetId as string),
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      shouldRetryOnError: false,
      dedupingInterval: 5 * 60_000,
      refreshInterval: 45 * 60_000,
      refreshWhenHidden: true,
    }
  );

  return { url: data ?? null, isLoading: Boolean(assetId) && isLoading, error };
}
